import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CalendarDays,
  MapPin,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { getEvents } from "../../api/events";
import { useLang } from "../../i18n/LanguageContext";

export const NextEventPopup = () => {
  const { lang } = useLang();

  const [event, setEvent] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let timer;

    const loadNextEvent = async () => {
      try {
        const data = await getEvents();

        if (!Array.isArray(data) || data.length === 0) {
          return;
        }

        const now = new Date();

        /*
         * On récupère les événements encore en cours
         * ou à venir.
         */
        const upcomingEvents = data
          .filter((item) => {
            if (!item.date_debut) {
              return false;
            }

            const startDate = new Date(item.date_debut);

            const endDate = item.date_fin
              ? new Date(item.date_fin)
              : startDate;

            return (
              item.statut === "A_VENIR" ||
              item.statut === "EN_COURS" ||
              endDate >= now
            );
          })
          .sort(
            (a, b) =>
              new Date(a.date_debut).getTime() -
              new Date(b.date_debut).getTime()
          );

        if (upcomingEvents.length === 0) {
          return;
        }

        /*
         * Le premier événement après le tri
         * est le prochain événement.
         */
        const nextEvent = upcomingEvents[0];

        setEvent(nextEvent);

        /*
         * Apparition légèrement différée pour
         * laisser la page se charger proprement.
         *
         * Le popup réapparaîtra à chaque
         * actualisation de la page.
         */
        timer = window.setTimeout(() => {
          setOpen(true);
        }, 900);
      } catch (error) {
        console.error("Unable to load next event popup.", error);
      }
    };

    loadNextEvent();

    return () => {
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, []);

  /*
   * Bloque le scroll de la page lorsque
   * le popup est ouvert.
   */
  useEffect(() => {
    if (!open) {
      return;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  /*
   * Ferme simplement le popup.
   *
   * Aucun localStorage :
   * il réapparaît donc après une actualisation.
   */
  const closePopup = () => {
    setOpen(false);
  };

  if (!event) {
    return null;
  }

  const startDate = new Date(event.date_debut);

  const formattedDate = startDate.toLocaleDateString(
    lang === "fr" ? "fr-FR" : "en-US",
    {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }
  );

  const formattedTime = startDate.toLocaleTimeString(
    lang === "fr" ? "fr-FR" : "en-US",
    {
      hour: "2-digit",
      minute: "2-digit",
    }
  );

  const imageUrl = event.image
    ? `${process.env.REACT_APP_STORAGE_URL}/${event.image}`
    : "/images/placeholder-event.jpg";

  const location = [
    event.lieu,
    event.ville,
    event.pays,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="
            fixed
            inset-0
            z-[9999]
            flex
            items-center
            justify-center
            px-4
            py-6
            sm:px-6
          "
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Fond assombri */}
          <motion.button
            type="button"
            aria-label={
              lang === "fr"
                ? "Fermer l'annonce"
                : "Close announcement"
            }
            onClick={closePopup}
            className="
              absolute
              inset-0
              w-full
              h-full
              bg-black/80
              backdrop-blur-md
              cursor-default
            "
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Fenêtre */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="next-event-title"
            initial={{
              opacity: 0,
              y: 35,
              scale: 0.97,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: 20,
              scale: 0.98,
            }}
            transition={{
              duration: 0.45,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="
              relative
              z-10
              w-full
              max-w-5xl
              max-h-[90vh]
              overflow-y-auto
              bg-dmi-charcoal
              text-white
              rounded-md
              shadow-2xl
              border
              border-white/10
            "
          >
            {/* Bouton fermeture */}
            <button
              type="button"
              onClick={closePopup}
              aria-label={
                lang === "fr"
                  ? "Fermer"
                  : "Close"
              }
              className="
                absolute
                top-4
                right-4
                z-30
                flex
                items-center
                justify-center
                w-10
                h-10
                rounded-full
                bg-black/50
                backdrop-blur-md
                border
                border-white/15
                text-white
                hover:bg-dmi-gold
                hover:text-dmi-charcoal
                transition-all
                duration-300
              "
            >
              <X size={19} />
            </button>

            <div className="grid lg:grid-cols-[0.95fr_1.05fr]">

              {/* Image */}
              <div className="relative min-h-[260px] lg:min-h-[580px] overflow-hidden">
                <img
                  src={imageUrl}
                  alt={event.titre}
                  className="
                    absolute
                    inset-0
                    w-full
                    h-full
                    object-cover
                  "
                />

                {/* Dégradé */}
                <div
                  className="
                    absolute
                    inset-0
                    bg-gradient-to-t
                    from-dmi-charcoal
                    via-dmi-charcoal/20
                    to-transparent
                    lg:bg-gradient-to-r
                    lg:from-transparent
                    lg:via-transparent
                    lg:to-dmi-charcoal/80
                  "
                />

                {/* Badge */}
                <div
                  className="
                    absolute
                    left-6
                    bottom-6
                    lg:left-8
                    lg:bottom-8
                  "
                >
                  <span
                    className="
                      inline-flex
                      items-center
                      bg-dmi-gold
                      text-dmi-charcoal
                      px-4
                      py-2
                      text-[10px]
                      font-medium
                      uppercase
                      tracking-[0.3em]
                    "
                  >
                    {lang === "fr"
                      ? "Prochain événement"
                      : "Next event"}
                  </span>
                </div>
              </div>

              {/* Contenu */}
              <div
                className="
                  relative
                  flex
                  flex-col
                  justify-center
                  px-6
                  py-9
                  sm:px-10
                  sm:py-12
                  lg:px-14
                  lg:py-16
                "
              >
                {/* Organisation */}
                <div
                  className="
                    text-dmi-gold
                    text-[10px]
                    uppercase
                    tracking-[0.35em]
                    mb-5
                  "
                >
                  Dalo Ministries International
                </div>

                {/* Titre */}
                <h2
                  id="next-event-title"
                  className="
                    font-serif
                    text-3xl
                    sm:text-4xl
                    lg:text-5xl
                    leading-[1.05]
                    text-white
                  "
                >
                  {event.titre}
                </h2>

                {/* Ligne dorée */}
                <div className="w-12 h-px bg-dmi-gold mt-7 mb-7" />

                {/* Date */}
                <div className="flex items-start gap-4">
                  <CalendarDays
                    size={18}
                    className="text-dmi-gold shrink-0 mt-0.5"
                  />

                  <div>
                    <div className="text-sm text-white/90 capitalize">
                      {formattedDate}
                    </div>

                    <div className="text-xs text-white/50 mt-1">
                      {formattedTime}
                    </div>
                  </div>
                </div>

                {/* Lieu */}
                {location && (
                  <div className="flex items-start gap-4 mt-5">
                    <MapPin
                      size={18}
                      className="text-dmi-gold shrink-0 mt-0.5"
                    />

                    <div className="text-sm text-white/70">
                      {location}
                    </div>
                  </div>
                )}

                {/* Description */}
                {event.description && (
                  <div
                    className="
                      mt-8
                      text-sm
                      sm:text-[15px]
                      leading-7
                      text-white/65
                      line-clamp-5
                    "
                  >
                    {event.description}
                  </div>
                )}

                {/* Actions */}
                <div
                  className="
                    flex
                    flex-col
                    sm:flex-row
                    sm:items-center
                    gap-4
                    mt-10
                  "
                >
                  {/* Détails événement */}
                  <Link
                    to={`/events/${event.id}`}
                    onClick={closePopup}
                    className="
                      btn-gold
                      inline-flex
                      items-center
                      justify-center
                      gap-2
                    "
                  >
                    {lang === "fr"
                      ? "Découvrir l'événement"
                      : "Discover the event"}

                    <ArrowUpRight size={17} />
                  </Link>

                  {/* Fermer */}
                  <button
                    type="button"
                    onClick={closePopup}
                    className="
                      inline-flex
                      items-center
                      justify-center
                      px-6
                      py-3
                      text-[11px]
                      uppercase
                      tracking-[0.22em]
                      text-white/60
                      hover:text-white
                      border
                      border-white/10
                      hover:border-white/30
                      transition-all
                      duration-300
                    "
                  >
                    {lang === "fr"
                      ? "J'ai lu — fermer"
                      : "I've read it — close"}
                  </button>
                </div>

              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};